// Shared display helpers for landlord-research results, used by both LeadEditPanel
// (the picker rows) and the AiResearchResult card, so the two surfaces render the
// same colours and labels (the lead pipeline's "surfaces that must agree" rule).

import type { AgenticMatch } from "@inrange/building-manager-api-client";

// The name a candidate renders as in the picker rows: `orgName ?? proprietorName`
// (null-coalesce, so an empty-string orgName is kept). Shared because three things
// must agree on it - the picker row (`agenticToOrgRow`), the "is my saved selection
// still in the list?" check (`isSelectedRepresented`), and the BE, which builds a
// persisted selection from it (`_candidate_display_name` in
// operation_landlord_research.py) and resolves a selection back to its candidate by
// the same rule when carrying one over. If these drift, a saved pick reads as
// "no longer in the current match list" while its row is sitting right there.
export const agenticDisplayName = (c: AgenticMatch): string =>
  c.orgName ?? c.proprietorName;

// Does this candidate correspond to a given party name? Matches on either the
// researched proprietor name or the platform-org name (a picker row's displayName
// is `orgName ?? proprietorName`), case-insensitive + trimmed. Shared so the tile's
// recommended-party pick and the modal's focused-candidate pick resolve
// `partyToContact` to the SAME candidate (surfaces-that-must-agree).
export const candidateMatchesName = (c: AgenticMatch, name: string): boolean => {
  const target = name.trim().toLowerCase();
  return (
    c.proprietorName.trim().toLowerCase() === target ||
    (c.orgName ?? "").trim().toLowerCase() === target
  );
};

// The candidate a run's decision recommends: the one matching `partyToContact`,
// else the top (highest-confidence, since the BE pre-sorts) candidate. Null when
// there are no candidates. The tile strip shows this candidate's confidence pill,
// and the modal falls back to it when no explicit row was clicked - so both name
// the same party.
//
// The top-candidate fallback skips `carriedForward` entries: those are the operator's
// selected party preserved from an EARLIER run (BMA `_preserve_selected_candidate`),
// and the list is confidence-sorted, so a carried entry outranking this run's
// candidates would otherwise be presented as what this run recommends. An explicit
// `partyToContact` match still wins - if the run names the carried party, that IS its
// recommendation.
export const recommendedCandidate = (
  candidates: AgenticMatch[],
  partyToContact: string | null | undefined
): AgenticMatch | null => {
  if (candidates.length === 0) return null;
  const party = (partyToContact ?? "").trim();
  if (party) {
    const m = candidates.find((c) => candidateMatchesName(c, party));
    if (m) return m;
  }
  return candidates.find((c) => !c.carriedForward) ?? candidates[0];
};

// Only http(s) links are safe to render as anchors from model-produced citations.
export const isSafeHttpUrl = (url: string): boolean =>
  /^https?:\/\//i.test(url.trim());

// The right-pane OrgSummary shows this line when an org cell has options but
// none is picked, or no options at all. Single-sourced so the left
// results-list Landlord column (LeadSearchPage / LeadsPage) renders the
// identical wording when a researched lead has no landlord selected and no HMLR
// top-pick, instead of a bare "-" that reads as "research never ran"
// (surfaces-that-must-agree).
export const noSelectionOptionsText = (optionCount: number): string =>
  optionCount > 0
    ? `${optionCount} option${optionCount === 1 ? "" : "s"}, none selected`
    : "No options available";

// Whether landlord research has run for this lead: it carries agentic candidate
// rows, a decision block, or a researched-at stamp. A NOT_ESTABLISHED run has no
// candidates but still a decision + stamp, so all three are checked.
export const landlordResearchHasRun = (signals: {
  agenticMatches?: unknown[] | null;
  agenticDecision?: unknown;
  agenticResearchedAt?: string | null;
}): boolean =>
  (signals.agenticMatches?.length ?? 0) > 0 ||
  signals.agenticDecision != null ||
  !!signals.agenticResearchedAt;

// Status text a left results-list Landlord column should show when the cell
// would otherwise be blank (no selected landlord, no HMLR top-pick). If research
// has run, mirror the right-pane OrgSummary status so the row never reads as
// "research didn't run"; otherwise null -> leave the cell blank as today.
// `landlordOptionCount` is the number of landlord candidate rows the right panel
// would show for the lead (HMLR-filtered-to-building + agentic), supplied by the
// caller since the HMLR building-overlap filter lives with each results table.
export const researchedBlankLandlordStatus = (
  signals: {
    agenticMatches?: unknown[] | null;
    agenticDecision?: unknown;
    agenticResearchedAt?: string | null;
  },
  landlordOptionCount: number
): string | null =>
  landlordResearchHasRun(signals)
    ? noSelectionOptionsText(landlordOptionCount)
    : null;

// Confidence -> a colour. >=70 green, >=40 amber, else red; null grey.
export const confColour = (pct: number | null): string =>
  pct == null
    ? "#6c757d"
    : pct >= 70
      ? "#198754"
      : pct >= 40
        ? "#d9822b"
        : "#d9534f";

// R24 verdict token -> operator label + colour.
export const verdictDisplay = (
  verdict: string | undefined | null
): { label: string; colour: string } => {
  switch (verdict) {
    case "PROCEED":
      return { label: "Proceed", colour: "#198754" };
    case "PROCEED_WITH_CAUTION":
      return { label: "Proceed with caution", colour: "#d9822b" };
    case "WITHHELD_AT_SOURCE":
      return { label: "Withheld at source", colour: "#6c757d" };
    case "INSUFFICIENT":
      return { label: "Insufficient evidence", colour: "#d9822b" };
    case "NOT_ESTABLISHED":
      return { label: "Not established", colour: "#6c757d" };
    default:
      return { label: verdict || "", colour: "#6c757d" };
  }
};

// R12 evidence tier -> a short colour for the citation-tier chip.
export const tierColour = (tier: string | undefined): string => {
  switch ((tier || "").toUpperCase()) {
    case "A":
      return "#198754";
    case "B":
      return "#0d6efd";
    case "C":
      return "#d9822b";
    default:
      return "#adb5bd";
  }
};

// R26 adjudication status -> label + colour.
export const adjudicationDisplay = (
  status: string
): { label: string; colour: string } => {
  switch (status) {
    case "CONFIRMED":
      return { label: "Confirmed", colour: "#198754" };
    case "CONTRADICTED":
      return { label: "Contradicted", colour: "#d9534f" };
    case "NO_EVIDENCE":
      return { label: "No evidence", colour: "#6c757d" };
    case "NOT_PURSUED":
      return { label: "Not pursued", colour: "#adb5bd" };
    default:
      return { label: status || "", colour: "#6c757d" };
  }
};

// A Companies House "Charges" filing lists the mortgages / legal securities a
// lender has registered against a company. The raw citation title the agent
// produces ("Charges for KEEL TOYS LIMITED (03744062) - Companies House") reads
// as jargon to an operator, so relabel it and explain what a charge is on hover.
const CH_CHARGES_LABEL = "Companies House charges (mortgages)";
const CH_CHARGES_EXPLAINER =
  "A charge is a mortgage or legal security a lender registers against a " +
  "company's property.";

// Turn a raw citation into the label + hover text to render for it. Pure so it
// can be unit-tested. Relabels Companies House charges pages (matched on the
// title text OR the /charges URL path); every other citation renders its own
// title (falling back to the URL), with the full title kept as hover text so a
// truncated one is still readable.
export const citationDisplay = (
  title: string | undefined | null,
  url: string
): { label: string; tooltip: string | null } => {
  const rawTitle = (title ?? "").trim();
  const isCharges =
    /charges\s+for/i.test(rawTitle) ||
    /\/charges(\/|\?|#|$)/i.test(url) ||
    /company-information\/[^/]+\/charges/i.test(url);
  if (isCharges) {
    return {
      label: CH_CHARGES_LABEL,
      // Keep the original title available on hover after the explainer.
      tooltip: rawTitle
        ? `${CH_CHARGES_EXPLAINER}\n\n${rawTitle}`
        : CH_CHARGES_EXPLAINER,
    };
  }
  return { label: rawTitle || url, tooltip: rawTitle || null };
};

// The model's reasoning occasionally references internal rule numbers ("demoted
// to Tier C per R12", "the R14 bar", "no R21 demotion applied") - internal
// scaffolding that must never reach an operator. Strip rule-number parentheticals
// and any standalone rule token, then tidy the seams. Pure so it can be
// unit-tested. Tier letters in prose go too (the tier is shown as its own A/B/C
// chip); a bare "Tier A or B source" aside is scaffolding, not information.
export const scrubResearchJargon = (text: string): string => {
  let out = text ?? "";
  // Drop any parenthetical aside that mentions a rule number or a tier letter,
  // e.g. "(demoted to Tier C per R12)", "(one Tier A or B source)".
  out = out.replace(/\s*\([^()]*\b(?:R\d+|Tier\s+[A-D])\b[^()]*\)/g, "");
  // Remove leftover inline references: "per R12", "the R14 bar", bare "R21".
  out = out.replace(/\b(?:per\s+)?R\d+\b(?:\s+bar)?/g, "");
  out = out.replace(/\bTier\s+[A-D]\b/g, "");
  // Tidy the seams the removals leave behind.
  out = out
    .replace(/\(\s*\)/g, "")
    .replace(/\s+([.,;:])/g, "$1")
    .replace(/\b(the|a|an|no)\s+([.,;:])/gi, "$2")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+\./g, ".")
    .trim();
  return out;
};

// Break a chunk of model prose into separate points, one per sentence / clause, so
// a run-on reasoning or next-action renders as several bullets instead of one wall.
// Splits on sentence terminators (". " / "! " / "? ") and semicolons - NOT on " - "
// dashes (the model uses those both as point separators AND as trailing clarifiers
// like "under its own name - owner-occupier signal", so splitting them fragments a
// point). Dotted abbreviations (i.e., e.g., Ltd., ...) are protected so a sentence
// split can't break "i.e. Coty" in two. Each point is jargon-scrubbed. Pure.
export const splitIntoPoints = (raw: string | null | undefined): string[] => {
  const text = (raw ?? "").replace(/\s+/g, " ").trim();
  if (!text) return [];
  const NUL = String.fromCharCode(0);
  // Hide the dots inside common abbreviations before the sentence split.
  const protectedText = text.replace(
    /\b(?:[a-z]\.[a-z]\.|etc\.|approx\.|no\.|vs\.|ltd\.|rd\.|st\.|ave\.|dr\.|mr\.|mrs\.|ms\.)/gi,
    (m) => m.split(".").join(NUL)
  );
  // Mark sentence ends (keep the terminator) and semicolons (drop them) as splits.
  const SPLIT = String.fromCharCode(1);
  const marked = protectedText
    .replace(/([.!?])\s+/g, `$1${SPLIT}`)
    .replace(/;\s*/g, SPLIT);
  return marked
    .split(SPLIT)
    .map((p) => scrubResearchJargon(p.split(NUL).join(".").trim()))
    .filter((p) => p.length > 0);
};

// The new-format candidate reasoning (R24) is two labelled, bulleted sections:
//   Why yes:
//   - <reason>
//   Why no:
//   - <gap>
// Parse it into the two bullet lists so AiResearchResult can render them as
// separate titled sections. The "Why yes:" / "Why no:" markers are matched
// case-insensitively; each section is then split into one point per sentence /
// clause (via splitIntoPoints), so a run-on section written before the bulleted
// prompt still renders as several bullets rather than one wall. Every point is
// jargon-scrubbed. Returns null for older single-blob reasoning (neither marker
// present) so callers can fall back to the legacy single-block rendering.
export const parseWhyYesNo = (
  reasoning: string
): { yes: string[]; no: string[] } | null => {
  const text = reasoning ?? "";
  const yesMatch = /why\s*yes\s*:/i.exec(text);
  const noMatch = /why\s*no\s*:/i.exec(text);
  if (!yesMatch && !noMatch) return null;

  // Each section runs from its marker to the start of the other marker (or the
  // end). Comparing the two marker indices makes either ordering parse correctly.
  const sectionAfter = (
    match: RegExpExecArray | null,
    other: RegExpExecArray | null
  ): string => {
    if (!match) return "";
    const start = match.index + match[0].length;
    const end = other && other.index > match.index ? other.index : text.length;
    return text.slice(start, end);
  };

  const toPoints = (segment: string): string[] => {
    // New format: the model already emitted "- " bullets. Take each bullet line
    // and split it further (a bullet can itself be a run-on). Old format: no
    // bullet lines, so split the whole run-on segment into sentence/clause points.
    const bulletLines = segment
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => /^[-*]\s+/.test(line))
      .map((line) => line.replace(/^[-*]\s+/, "").trim());
    const rawPoints = bulletLines.length > 0 ? bulletLines : [segment];
    return rawPoints.flatMap(splitIntoPoints);
  };

  return {
    yes: toPoints(sectionAfter(yesMatch, noMatch)),
    no: toPoints(sectionAfter(noMatch, yesMatch)),
  };
};

// A short, decision-ready one-liner for a candidate row: jargon-scrubbed, first
// sentence only, clamped at a word boundary. The full (scrubbed) reasoning stays
// available in the Evidence modal - the row is for scanning, not reading.
export const shortReason = (
  text: string | undefined | null,
  maxLen = 150
): string => {
  const raw = (text ?? "").trim();
  // New-format reasoning ("Why yes:\n- ...\nWhy no:\n- ...") would otherwise clamp
  // to an ugly "Why yes: -" prefix; show its first Why yes bullet (the strongest
  // supporting reason) instead, falling back to the first Why no bullet.
  const parsed = parseWhyYesNo(raw);
  const source =
    parsed && (parsed.yes.length > 0 || parsed.no.length > 0)
      ? parsed.yes[0] ?? parsed.no[0] ?? ""
      : raw;
  const scrubbed = scrubResearchJargon(source);
  if (!scrubbed) return "";
  // Prefer the first sentence when it lands before the clamp.
  const stop = scrubbed.search(/[.!?](\s|$)/);
  let out = stop > 0 && stop + 1 <= maxLen ? scrubbed.slice(0, stop + 1) : scrubbed;
  if (out.length > maxLen) {
    const clipped = out.slice(0, maxLen);
    const lastSpace = clipped.lastIndexOf(" ");
    out = `${(lastSpace > 40 ? clipped.slice(0, lastSpace) : clipped).trim()}…`;
  }
  return out;
};
